/**
 * Notification Sound Service for HáLubri
 * Different sounds for each user profile
 */

// Audio context for generating sounds programmatically
let audioContext = null;

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

// Sound configurations for each profile
const SOUND_CONFIGS = {
  // Cliente - Soft, pleasant notification (order updates)
  cliente: {
    frequencies: [523.25, 659.25, 783.99], // C5, E5, G5 - Major chord
    duration: 0.15,
    type: 'sine',
    volume: 0.3,
    delay: 0.1
  },
  
  // Funcionário - Alert sound for new orders (urgent but professional)
  funcionario: {
    frequencies: [440, 554.37, 659.25, 880], // A4, C#5, E5, A5 - Rising alert
    duration: 0.12,
    type: 'square',
    volume: 0.25,
    delay: 0.08
  },
  
  // Entregador - Dynamic, energetic sound for new deliveries
  entregador: {
    frequencies: [329.63, 415.30, 523.25, 659.25], // E4, G#4, C5, E5 - Upbeat
    duration: 0.1,
    type: 'sawtooth',
    volume: 0.2,
    delay: 0.07
  },
  
  // Success sound (order completed, delivery done)
  success: {
    frequencies: [523.25, 659.25, 783.99, 1046.50], // C5, E5, G5, C6
    duration: 0.2,
    type: 'sine',
    volume: 0.25,
    delay: 0.15
  },
  
  // Chat message received
  chat: {
    frequencies: [880, 1108.73], // A5, C#6 - Short ping
    duration: 0.08,
    type: 'sine',
    volume: 0.2,
    delay: 0.05
  }
};

/**
 * Play a sequence of tones
 */
const playToneSequence = (config) => {
  try {
    const ctx = getAudioContext();
    
    // Resume audio context if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    
    const { frequencies, duration, type, volume, delay } = config;
    
    frequencies.forEach((freq, index) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(freq, ctx.currentTime);
      
      const startTime = ctx.currentTime + (index * delay);
      const endTime = startTime + duration;
      
      // Envelope for smooth sound
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(volume * 0.8, endTime - 0.02);
      gainNode.gain.linearRampToValueAtTime(0, endTime);
      
      oscillator.start(startTime);
      oscillator.stop(endTime + 0.1);
    });
    
    return true;
  } catch (error) {
    console.warn('Could not play notification sound:', error);
    return false;
  }
};

/**
 * Play notification sound for a specific profile
 * @param {string} profile - 'cliente', 'funcionario', 'entregador', 'success', 'chat'
 */
export const playNotificationSound = (profile = 'cliente') => {
  const config = SOUND_CONFIGS[profile] || SOUND_CONFIGS.cliente;
  return playToneSequence(config);
};

/**
 * Play client notification sound (order updates)
 */
export const playClientNotification = () => playNotificationSound('cliente');

/**
 * Play employee notification sound (new orders)
 */
export const playEmployeeNotification = () => playNotificationSound('funcionario');

/**
 * Play driver notification sound (new deliveries)
 */
export const playDriverNotification = () => playNotificationSound('entregador');

/**
 * Play success sound (completed actions)
 */
export const playSuccessSound = () => playNotificationSound('success');

/**
 * Play chat message sound
 */
export const playChatSound = () => playNotificationSound('chat');

/**
 * Initialize audio context on user interaction
 * Call this on first user click to enable sounds
 */
export const initializeAudio = () => {
  try {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    // Play a silent sound to unlock audio
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.001);
    return true;
  } catch (error) {
    console.warn('Could not initialize audio:', error);
    return false;
  }
};

/**
 * Check if sounds are enabled in localStorage
 */
export const areSoundsEnabled = () => {
  const setting = localStorage.getItem('halubri_sounds_enabled');
  return setting !== 'false'; // Enabled by default
};

/**
 * Toggle sounds on/off
 */
export const toggleSounds = (enabled) => {
  localStorage.setItem('halubri_sounds_enabled', enabled ? 'true' : 'false');
  return enabled;
};

/**
 * Play sound if enabled
 */
export const playIfEnabled = (soundFn) => {
  if (areSoundsEnabled()) {
    return soundFn();
  }
  return false;
};

export default {
  playNotificationSound,
  playClientNotification,
  playEmployeeNotification,
  playDriverNotification,
  playSuccessSound,
  playChatSound,
  initializeAudio,
  areSoundsEnabled,
  toggleSounds,
  playIfEnabled
};
